/**
 * Friendly facet names in URLs: /blast?custitem_felec=120-VAC  ->  /blast?power-source=120-VAC
 *
 * The core translator builds every facet link (filters, chips, sort, paging,
 * the address bar on click) from the field id, and SuiteCommerce Configuration
 * has no URL-alias column for facets. So:
 *   - outbound: Translator.getUrl() output has facet ids swapped for aliases
 *     (installed by Clemtex.Brands.Browse, after its own brand rewrite);
 *   - inbound: Backbone.History.loadUrl() hands the router the id form, so the
 *     core parses an aliased URL exactly as it parses the old one, while the
 *     address bar keeps the alias;
 *   - old links (custitem_...) keep working; on arrival the address bar is
 *     quietly replaced with the aliased form (replaceState, no history entry).
 *
 * Only facet NAMES are aliased. Values keep the platform's own URL tokens
 * (Air-Systems, 50-ft, 3-SLASH-8, GVS~RPB), which the core already produces.
 * Pure helpers are exported for the unit tests.
 */
define('Clemtex.Brands.FacetUrls', [
    'underscore'
], function (_) {
    'use strict';

    // Translator option parameters and API names a facet alias must never shadow.
    var RESERVED = ['order', 'page', 'show', 'display', 'keywords', 'sort', 'q', 'category',
        'commercecategoryname', 'commercecategoryurl', 'fieldset', 'include', 'limit', 'offset'];
    var ALIAS_RE = /^[a-z0-9][a-z0-9-]*$/;

    // rows: [{ facetId, alias }] from the configuration table.
    // Invalid, reserved, duplicate or id-colliding rows are skipped (and reported).
    function buildAliasMap(rows) {
        var idToAlias = {};
        var aliasToId = {};
        var skipped = [];
        var ids = {};
        _.each(rows || [], function (row) {
            var id = row && String(row.facetId || '').trim();
            if (id) { ids[id.toLowerCase()] = true; }
        });
        _.each(rows || [], function (row) {
            var id = row && String(row.facetId || '').trim();
            var alias = row && String(row.alias || '').trim().toLowerCase();
            if (!id || !alias) { return; }
            var why = !ALIAS_RE.test(alias) ? 'invalid characters'
                : _.contains(RESERVED, alias) ? 'reserved name'
                    : ids[alias] ? 'collides with a facet id'
                        : aliasToId[alias] ? 'duplicate alias'
                            : idToAlias[id] ? 'facet already aliased' : null;
            if (why) { skipped.push(id + ' -> ' + alias + ' (' + why + ')'); return; }
            idToAlias[id] = alias;
            aliasToId[alias] = id;
        });
        return { idToAlias: idToAlias, aliasToId: aliasToId, skipped: skipped, empty: _.isEmpty(idToAlias) };
    }

    // Rename query-string keys through lookup(key) -> newKey|undefined. Order,
    // values and everything outside the query string are left byte-for-byte.
    function renameKeys(url, lookup) {
        if (typeof url !== 'string' || url.indexOf('?') === -1) { return url; }
        var hashAt = url.indexOf('#');
        var hash = hashAt === -1 ? '' : url.substring(hashAt);
        var body = hashAt === -1 ? url : url.substring(0, hashAt);
        var q = body.indexOf('?');
        var path = body.substring(0, q);
        var query = body.substring(q + 1);
        if (!query) { return url; }
        var changed = false;
        var parts = _.map(query.split('&'), function (pair) {
            var eq = pair.indexOf('=');
            var key = eq === -1 ? pair : pair.substring(0, eq);
            var renamed = lookup(key);
            if (!renamed || renamed === key) { return pair; }
            changed = true;
            return renamed + (eq === -1 ? '' : pair.substring(eq));
        });
        return changed ? path + '?' + parts.join('&') + hash : url;
    }

    function aliasUrl(url, map) {
        if (!map || map.empty) { return url; }
        return renameKeys(url, function (key) { return map.idToAlias[key]; });
    }

    function dealiasUrl(url, map) {
        if (!map || map.empty) { return url; }
        return renameKeys(url, function (key) { return map.aliasToId[String(key).toLowerCase()]; });
    }

    function hasAliasableKey(url, map) {
        return !!map && !map.empty && aliasUrl(url, map) !== url;
    }

    // Inbound: route on the id form; keep (or switch to) the alias in the address bar.
    var historyInstalled = false;
    function installHistory(Backbone, map) {
        if (historyInstalled || !map || map.empty || !Backbone || !Backbone.History) { return; }
        historyInstalled = true;
        var proto = Backbone.History.prototype;
        var originalLoadUrl = proto.loadUrl;
        proto.loadUrl = function loadUrl(fragment) {
            var current = this.getFragment(fragment);
            var routed = dealiasUrl(current, map);
            var onServer = window.SC && SC.ENVIRONMENT && SC.ENVIRONMENT.jsEnvironment === 'server';
            // A prerendered page for an aliased URL may have been rendered by a
            // prerenderer that does not know the aliases (unfiltered markup).
            // Don't bind to it: render fresh so the results always match the URL.
            if (routed !== current && !onServer && window.SC && SC.isRehydrating) { SC.isRehydrating = false; }
            var result = originalLoadUrl.call(this, routed);
            try {
                var here = window.location.pathname + window.location.search;
                if (!onServer && this._hasPushState && window.history && window.history.replaceState && hasAliasableKey(here, map)) {
                    window.history.replaceState(window.history.state, document.title, aliasUrl(here, map) + window.location.hash);
                }
            } catch (e) { /* cosmetic only */ }
            return result;
        };
    }

    return {
        RESERVED: RESERVED,
        buildAliasMap: buildAliasMap,
        aliasUrl: aliasUrl,
        dealiasUrl: dealiasUrl,
        hasAliasableKey: hasAliasableKey,
        installHistory: installHistory
    };
});
